import React, { useState } from 'react';

import { Loading } from '../../components/Loading/Loading';
import { useGetAnalystMetricsQuery } from '../../redux/services/AnalystApi';
import { type AnalystFiltersParams, type AnalystMetrics as AnalystMetricsData, type CurrencyRow, type DailyRow, type GroupRow } from '../../redux/types/AnalystTypes';
import { AnalystPage } from './components/AnalystPage';
import { DailyChart } from './components/DailyChart';
import { PeriodFilters } from './components/PeriodFilters';
import { type Column, SortableTable } from './components/SortableTable';
import { errorText, fmt, fmtInt, monthStart, toneOf, today } from './helpers';
import styles from './Analyst.module.css';

type PayoutRow = AnalystMetricsData['payouts']['by_currency'][number];
type Tab = 'merchant' | 'trader' | 'method' | 'currency' | 'daily';
const TABS: Array<{ id: Tab; name: string }> = [
  { id: 'merchant', name: 'По мерчантам' },
  { id: 'trader', name: 'По трейдерам' },
  { id: 'method', name: 'По методам' },
  { id: 'currency', name: 'По валютам' },
  { id: 'daily', name: 'По дням' },
];

const Card = ({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) => (
  <div className={styles.card}>
    <div className={styles.cardLabel}>{label}</div>
    <div className={`${styles.cardValue} ${tone ? styles[tone] : ''}`}>{value}</div>
    {sub && <div className={styles.cardSub}>{sub}</div>}
  </div>
);

const money = <T,>(key: string, name: string, get: (row: T) => string, tone = false): Column<T> => ({
  key,
  name,
  numeric: true,
  value: get,
  render: (row) => <span className={tone ? styles[toneOf(get(row))] : ''}>{fmt(get(row))}</span>,
});

const groupColumns = (name: string): Array<Column<GroupRow>> => [
  { key: 'label', name, value: (r) => r.label ?? (r.key === null ? '—' : String(r.key)) },
  { key: 'created', name: 'Создано', numeric: true, value: (r) => r.created, render: (r) => fmtInt(r.created) },
  { key: 'success', name: 'Оплачено', numeric: true, value: (r) => r.success_count, render: (r) => fmtInt(r.success_count) },
  { key: 'conv', name: 'Конверсия без NF, %', numeric: true, value: (r) => r.conversion_without_nf_percent, render: (r) => fmt(r.conversion_without_nf_percent) },
  money('gross', 'Оборот, USDT', (r) => r.gross_usdt),
  money('fee', 'Комиссия мерчанта, USDT', (r) => r.merchant_fee_usdt),
  money('trader', 'Списано с трейдеров, USDT', (r) => r.trader_charged_usdt),
  money('merchant', 'Зачислено мерчантам, USDT', (r) => r.merchant_credited_usdt),
  money('profit', 'Прибыль, USDT', (r) => r.profit_usdt, true),
  { key: 'margin', name: 'Маржа, %', numeric: true, value: (r) => r.margin_percent, render: (r) => fmt(r.margin_percent) },
  money('avg', 'Средний чек, USDT', (r) => r.avg_check_usdt),
  { key: 'avgp', name: 'Прибыль на заявку, USDT', numeric: true, value: (r) => r.avg_profit_usdt, render: (r) => fmt(r.avg_profit_usdt, 3) },
];

const currencyColumns: Array<Column<CurrencyRow>> = [
  { key: 'currency', name: 'Валюта', value: (r) => r.currency },
  { key: 'success', name: 'Оплачено', numeric: true, value: (r) => r.success_count, render: (r) => fmtInt(r.success_count) },
  money('fiat', 'Оборот в валюте', (r) => r.turnover_fiat),
  money('avgf', 'Средний чек в валюте', (r) => r.avg_check_fiat),
  money('gross', 'Оборот, USDT', (r) => r.gross_usdt),
  money('trader', 'Списано с трейдеров, USDT', (r) => r.trader_charged_usdt),
  money('merchant', 'Зачислено мерчантам, USDT', (r) => r.merchant_credited_usdt),
  money('profit', 'Прибыль, USDT', (r) => r.profit_usdt, true),
  { key: 'margin', name: 'Маржа, %', numeric: true, value: (r) => r.margin_percent, render: (r) => fmt(r.margin_percent) },
];

const dailyColumns: Array<Column<DailyRow>> = [
  { key: 'date', name: 'День', value: (r) => r.date },
  { key: 'created', name: 'Создано', numeric: true, value: (r) => r.created, render: (r) => fmtInt(r.created) },
  { key: 'nf', name: 'Без реквизита (NF)', numeric: true, value: (r) => r.not_found, render: (r) => fmtInt(r.not_found) },
  { key: 'success', name: 'Оплачено', numeric: true, value: (r) => r.success_count, render: (r) => fmtInt(r.success_count) },
  { key: 'conv', name: 'Конверсия без NF, %', numeric: true, value: (r) => r.conversion_without_nf_percent, render: (r) => fmt(r.conversion_without_nf_percent) },
  money('gross', 'Оборот, USDT', (r) => r.gross_usdt),
  money('profit', 'Прибыль с пополнений', (r) => r.profit_usdt, true),
  money('payout', 'Прибыль с выплат', (r) => r.payout_profit_usdt, true),
  money('total', 'Прибыль всего, USDT', (r) => r.total_profit_usdt, true),
];

export const AnalystMetrics = () => {
  const [filters, setFilters] = useState<AnalystFiltersParams>({ date_from: monthStart(), date_to: today() });
  const [tab, setTab] = useState<Tab>('merchant');
  const { data, isFetching, isError, error } = useGetAnalystMetricsQuery(filters, {
    skip: !filters.date_from || !filters.date_to,
  });
  const o = data?.orders;

  return (
    <AnalystPage
      title='Метрики'
      hint={
        <>
          Прибыль с пополнения = списано с трейдера − зачислено мерчанту (по курсам и ставкам, зафиксированным в заявке).
          Прибыль с выплаты = комиссия площадки по выплате. Период — по дате создания заявки, время сервера (МСК).
        </>
      }
    >
      <PeriodFilters value={filters} onChange={setFilters} />
      {isError && <div className={styles.error}>{errorText(error)}</div>}
      {!data && isFetching && <Loading />}
      {data && o && (
        <div style={{ opacity: isFetching ? 0.55 : 1 }}>
          <div className={styles.cards}>
            <Card label='Прибыль всего, USDT' value={fmt(data.total_profit_usdt)} tone={toneOf(data.total_profit_usdt)}
              sub={`пополнения ${fmt(data.payin.profit_usdt)} · выплаты ${fmt(data.payouts.profit_usdt)}`} />
            <Card label='Оборот пополнений, USDT' value={fmt(data.payin.gross_usdt)} sub={`по курсу мерчанта до комиссии`} />
            <Card label='Оплачено заявок' value={fmtInt(o.success)} sub={`из ${fmtInt(o.created)} созданных`} />
            <Card label='Конверсия' value={`${fmt(o.conversion_without_nf_percent)} %`} sub={`без NF; с NF — ${fmt(o.conversion_percent)} %`} />
            <Card label='Средний чек, USDT' value={fmt(data.payin.avg_check_usdt)} sub={`прибыль на заявку ${fmt(data.payin.avg_profit_usdt, 3)}`} />
            <Card label='Маржа пополнений' value={`${fmt(data.payin.margin_percent)} %`} sub='прибыль / оборот' />
            <Card label='Комиссия мерчантов, USDT' value={fmt(data.payin.merchant_fee_usdt)} sub={`зачислено ${fmt(data.payin.merchant_credited_usdt)}`} />
            <Card label='Списано с трейдеров, USDT' value={fmt(data.payin.trader_charged_usdt)} />
            <Card label='Среднее время до итога' value={o.avg_minutes_to_final === null ? '—' : `${fmt(o.avg_minutes_to_final, 1)} мин`} sub='от создания до последнего изменения оплаченной' />
            <Card label='Без реквизита (NF)' value={fmtInt(o.not_found)} sub={`неуспешных ${fmtInt(o.failed)} · в работе ${fmtInt(o.in_work)}`} />
            <Card label='Перерасчёты и споры' value={`${fmtInt(o.recalculated)} / ${fmtInt(o.disputed)}`} sub='FR / заявок со спором' />
            <Card label='Выплаты' value={`${fmtInt(data.payouts.success_count)} из ${fmtInt(data.payouts.created)}`} sub={`долг мерчантов ${fmt(data.payouts.merchant_debt_usdt)} USDT`} />
          </div>

          <h2>Прибыль по дням</h2>
          <DailyChart rows={data.daily} />

          <div className={styles.tabs}>
            {TABS.map((t) => (
              <button key={t.id} type='button' className={tab === t.id ? styles.tabActive : styles.tab} onClick={() => setTab(t.id)}>
                {t.name}
              </button>
            ))}
          </div>
          {tab === 'merchant' && <SortableTable rows={data.by_merchant} columns={groupColumns('Мерчант')} rowKey={(r) => String(r.key)} />}
          {tab === 'trader' && <SortableTable rows={data.by_trader} columns={groupColumns('Трейдер')} rowKey={(r) => String(r.key)} />}
          {tab === 'method' && <SortableTable rows={data.by_method} columns={groupColumns('Метод')} rowKey={(r) => String(r.key)} />}
          {tab === 'currency' && (
            <>
              <SortableTable rows={data.by_currency} columns={currencyColumns} rowKey={(r) => String(r.currency)} />
              <h2>Выплаты по валютам</h2>
              <SortableTable
                rows={data.payouts.by_currency}
                rowKey={(r) => String(r.currency)}
                columns={[
                  { key: 'currency', name: 'Валюта', value: (r: PayoutRow) => r.currency },
                  { key: 'count', name: 'Успешных', numeric: true, value: (r) => r.success_count, render: (r) => fmtInt(r.success_count) },
                  money<PayoutRow>('fiat', 'Сумма в валюте', (r) => r.amount_fiat),
                  money<PayoutRow>('debt', 'Долг мерчантов, USDT', (r) => r.merchant_debt_usdt),
                  money<PayoutRow>('trader', 'Трейдерам, USDT', (r) => r.trader_usdt),
                  money<PayoutRow>('profit', 'Прибыль, USDT', (r) => r.profit_usdt, true),
                ]}
              />
            </>
          )}
          {tab === 'daily' && <SortableTable rows={data.daily} columns={dailyColumns} rowKey={(r) => r.date} />}
        </div>
      )}
    </AnalystPage>
  );
};
